import CountUp from "react-countup";
import { motion } from "framer-motion";
import { Rocket, TrendingDown, BarChart3, Brain, Layers } from "lucide-react";

const stats = [
  { value: 20, suffix: "+", label: "Years Industry Experience" },
  { value: 100, suffix: "+", label: "Successful Projects" },
  { value: 99.9, suffix: "%", decimals: 1, label: "System Availability" },
  { value: 24, suffix: "/7", label: "Enterprise Support" },
];

const benefits = [
  { icon: Rocket, title: "Faster Deployment", desc: "Cut time-to-production by up to 60% with our reference architectures." },
  { icon: TrendingDown, title: "Reduced Operational Cost", desc: "AI-driven automation that lowers OPEX while improving reliability." },
  { icon: BarChart3, title: "Increased Productivity", desc: "Augment teams with copilots and agents purpose-built for your SOPs." },
  { icon: Brain, title: "Data-Driven Decisions", desc: "Live dashboards and predictive analytics turn data into action." },
  { icon: Layers, title: "Scalable Architecture", desc: "Kubernetes-native, edge-aware platforms that scale with you." },
];

export default function WhyChooseUs() {
  return (
    <section id="why-choose-us" data-testid="why-choose-us-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-cyan-400 font-semibold mb-3">
              Why Choose Spoorti
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Battle-tested engineering.
              <br />
              <span className="gradient-text-vivid">Enterprise outcomes.</span>
            </h2>
            <p className="mt-4 text-slate-300 text-base sm:text-lg">
              Certified Cloud &amp; Kubernetes experts delivering for manufacturing, healthcare,
              education, smart cities and energy — at scale.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-6">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="bg-[#131C2F] border border-white/10 rounded-2xl p-5"
                  data-testid={`stat-${i}`}
                >
                  <div className="font-heading text-4xl sm:text-5xl font-bold gradient-text-vivid">
                    <CountUp
                      end={s.value}
                      decimals={s.decimals || 0}
                      duration={2.2}
                      enableScrollSpy
                      scrollSpyOnce
                    />
                    {s.suffix}
                  </div>
                  <div className="mt-1 text-sm text-slate-400">{s.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-5">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.07, duration: 0.5 }}
                  className="card-hover bg-[#131C2F] border border-white/10 rounded-2xl p-6"
                >
                  <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/20 grid place-items-center">
                    <Icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-semibold">{b.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{b.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
